/**
 * @param {Index} ix
 * @param {PageSize} pageSize
 * @returns {PageNumber}
 */
export const calculatePageContainingIndex = (ix, pageSize) => {
    return Math.floor(ix / pageSize) + 1;
};

/**
 * @param {PageNumber} page
 * @param {PageSize} pageSize
 * @returns {Index}
 */
export const calculatePageOffset = (page, pageSize) => {
    return (page - 1) * pageSize;
};

/**
 * @param {PageNumber} page
 * @param {PageSize} pageSize
 * @param {number} total
 */
export const calculatePageRange = (page, pageSize, total) => {
    const start = calculatePageOffset(page, pageSize);
    const end = Math.min(start + pageSize, total);
    return [start, end];
};

/**
 * @template {PaginationState} P
 * @param {P} state
 * @param {PageNumber[]} pageNumbers
 * @returns {P}
 */
export function updateWithPageRequests(state, pageNumbers) {
    const pagesClone = { ...state.pages };
    for (const pageNumber of pageNumbers) {
        pagesClone[pageNumber] = { status: 'loading' };
    }
    return {
        ...state,
        pages: pagesClone
    };
}

/**
 * @template {PaginationState} P
 * @param {P} state
 * @param {PageNumber} pageNumber
 * @returns {P}
 */
export function updateWithPageError(state, pageNumber) {
    return {
        ...state,
        pages: {
            ...state.pages,
            [pageNumber]: { status: 'error' }
        }
    };
}

/**
 * @template {PaginationState} P
 * @param {P} state
 * @param {PageLoadPayload} payload
 * @returns {P}
 */
export function updateWithPageLoad(state, payload) {
    const { pageNumber, items, total } = payload;
    const ids = items.map(item => item.id);

    return {
        ...state,
        pages: {
            ...state.pages,
            [pageNumber]: { status: 'loaded', items: ids }
        },
        total
    };
}

/**
 * @param {PaginationState} state
 * @param {PageSize} pageSize
 * @param {(id: string) => import("./items").PlanPickerItem} getItem
 */
export function toItemArray(state, pageSize, getItem) {
    const { total, pages } = state;
    if (total === 'unknown') return [];

    /** @type {import("./items").PlanPickerItemWithStatus[]} */
    let items = new Array(total);

    const pageCount = Math.ceil(total / pageSize);
    for (let p = 1; p <= pageCount; p++) {
        const page = pages[p];
        const [start, end] = calculatePageRange(p, pageSize, total);

        if (!page || page.status === 'error') {
            for (let i = start; i < end; i++) {
                items[i] = { status: 'unloaded', ix: i };
            }
        } else if (page.status === 'loading') {
            for (let i = start; i < end; i++) {
                items[i] = { status: 'loading', ix: i };
            }
        } else {
            page.items.forEach((id, i) => {
                items[i + start] = getItem(id);
            });
        }
    }

    return items;
}

/**
 * @param {PaginationState} state
 * @param {PageSize} pageSize
 * @param {Index[]} indexesToLoad
 * @returns {PageNumber[]}
 */
export function determinePagesToLoad(state, pageSize, indexesToLoad) {
    /** @type {Set<number>} */
    // eslint-disable-next-line no-undef
    const pagesToLoad = new Set();

    for (const i of indexesToLoad) {
        const pageNumber = calculatePageContainingIndex(i, pageSize);
        const page = state.pages[pageNumber];

        if (!page || page.status === 'error') {
            pagesToLoad.add(pageNumber);
        }
    }

    return [...pagesToLoad];
}

/**
 * @typedef {object} PaginationState
 * @prop {Record<PageNumber, Page>} pages
 * @prop {number | 'unknown'} total
 */

/**'
 * @typedef {{ status: 'loading' } | { status: 'error' } | { status: 'loaded', items: string[] }} Page
 */

/**
 * @typedef {object} PageLoadPayload
 * @prop {string} [query]
 * @prop {PageNumber} pageNumber
 * @prop {number} total
 * @prop {import("./items").RawPlanPickerItem[]} items
 */

/** @typedef {number} PageNumber */
/** @typedef {number} Index */
/** @typedef {[start: Index, end: Index]} Range */
/** @typedef {number} PageSize */
